// A post's header as chips (docs/news/SPEC.md, decision 17): the leading emoji and a first line made only
// of hashtags leave the text; a hashtag inside a sentence stays in it and also becomes a chip.

const LEADING_EMOJI = /^[^\p{L}\p{N}#\n]*/u;
const HASHTAG_LINE = /^(?:#[\p{L}\p{N}_]+[ \t]*)+$/u;
const HASHTAG = /#([\p{L}\p{N}_]+)/gu;

/**
 * @param {string} text plain text of the post
 * @param {string[]} mine lower-case hashtags that are the user's tickers
 * @returns {{ emoji: string[], tags: { label: string, mine: boolean }[], body: string }}
 */
export function splitPost(text, mine = []) {
  const lead = text.match(LEADING_EMOJI)[0];
  let body = text.slice(lead.length);
  const newline = body.indexOf('\n');
  const firstLine = (newline === -1 ? body : body.slice(0, newline)).trim();
  if (HASHTAG_LINE.test(firstLine)) {
    body = newline === -1 ? '' : body.slice(newline + 1);
  }

  const seen = new Set();
  const tags = [];
  for (const [label, name] of text.matchAll(HASHTAG)) {
    const key = name.toLowerCase();
    if (!seen.has(key)) {
      seen.add(key);
      tags.push({ label, mine: mine.includes(key) });
    }
  }

  // The user's tickers go first (decision 17); the rest keep the post's order.
  tags.sort((a, b) => Number(b.mine) - Number(a.mine));

  return { emoji: splitEmoji(lead.trim()), tags, body: body.trim() };
}

/**
 * The body cut into text and links (decision 25): each link's text is looked up after the previous one,
 * so repeated words are matched in order; a link whose text is not found is skipped.
 *
 * @param {string} body
 * @param {{ text: string, url: string }[]} links
 * @returns {{ text: string, url?: string }[]}
 */
export function linkSegments(body, links = []) {
  const segments = [];
  let from = 0;
  for (const link of links) {
    if (!/^https?:\/\//i.test(link.url)) {
      continue;
    }
    const at = body.indexOf(link.text, from);
    if (at === -1) {
      continue;
    }
    if (at > from) {
      segments.push({ text: body.slice(from, at) });
    }
    segments.push({ text: link.text, url: link.url });
    from = at + link.text.length;
  }
  if (from < body.length) {
    segments.push({ text: body.slice(from) });
  }
  return segments;
}

// One emoji per circle: "⚠️🇷🇺" → ["⚠️", "🇷🇺"] (a flag or ⚠️ with its variation selector is one symbol).
function splitEmoji(text) {
  if (text === '') {
    return [];
  }
  const graphemes = typeof Intl.Segmenter === 'function'
    ? Array.from(new Intl.Segmenter(undefined, { granularity: 'grapheme' }).segment(text), (s) => s.segment)
    : [text];

  return graphemes.filter((g) => g.trim() !== '');
}
